// src/pages/client/ClientDashboard.jsx
// FASE 1.C — Vista cliente. Reusaba LivePage con un header simple.
// FASE 4 UX — Mismo layout que AdminDashboard (Sidebar + Topbar + dark mode +
// notificaciones), pero sin items admin-only:
//   - Sidebar con isAdmin={false} → oculta "Performance Lab" y "Configuración"
//   - Páginas accesibles: 'live' (default) y 'signals'
//   - Si localStorage tiene una página admin-only residual (residuo de cuando
//     el user fue admin), se fuerza a 'live' al montar.
// UX audit ISSUE C: ahora el componente se monta en 2 rutas distintas
//   - /dashboard → page='live' (por defecto)
//   - /historial  → page='signals' (deeplink)
// La navegación entre pestañas usa navigate() en lugar de setPage local.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import '../../index.css';
import Sidebar from '../../components/Sidebar';
import Topbar from '../../components/Topbar';
import LivePage from '../../components/LivePage';
import SignalsHistory from '../../components/SignalsHistory';
import OnboardingTour, { TOUR_STORAGE_KEY } from '../../components/OnboardingTour';
import { useAuth } from '../../auth/AuthContext';
import { normalizeLeague } from '../../utils';
import { DashboardThemeProvider } from '../../theme/DashboardTheme';

const CLIENT_PAGES = new Set(['live', 'signals']);

// ── Banner de pausa técnica del motor V6 ────────────────────────
// Mientras este toggle esté en true se renderiza el banner naranja
// arriba del contenido cliente avisando que los picks llegan
// manualmente por Telegram. Para reactivar el motor:
//   1. Cambiar a `false` y guardar
//   2. cd dashboard/edge-elite && npm run build
//   3. firebase deploy --only hosting --project mati-edge-elite
const PAUSA_TECNICA_ACTIVA = false;

function PausaTecnicaBanner() {
  return (
    <div
      role="status"
      style={{
        background: '#FF6B2C',
        color: '#FFFFFF',
        padding: '12px 16px',
        margin: '0 0 12px 0',
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.45,
        boxShadow: '0 1px 4px rgba(255,107,44,0.25)',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        ⏸️ PAUSA TÉCNICA — Motor V6 en recalibración
      </div>
      <div style={{ fontSize: 12, opacity: 0.95 }}>
        Los picks llegan por <strong>Telegram VIP manualmente</strong>. Cada uno aparece
        en el historial con el badge naranja <strong>“📍 PICK MANUAL”</strong>. Verificá los
        resultados ahí.
      </div>
    </div>
  );
}

// UX audit ISSUE C: ClientDashboard se monta tanto en /dashboard como en
// /historial. La prop `initialPage` decide la pestaña inicial. Si viene,
// SOBREESCRIBE el localStorage (la URL es más autoritativa que el caché).
export default function ClientDashboard({ initialPage }) {
  const navigate = useNavigate();
  const [picks, setPicks]           = useState([]);
  const [connected, setConnected]   = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [page, setPage]             = useState(() => {
    if (initialPage && CLIENT_PAGES.has(initialPage)) return initialPage;
    const saved = localStorage.getItem('ee_route') || 'live';
    return CLIENT_PAGES.has(saved) ? saved : 'live';
  });
  const [selectedLeague, setLeague] = useState(() => localStorage.getItem('ee_league') || 'all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tick, setTick]             = useState(0);
  // Lazy initializer: chequea localStorage UNA SOLA VEZ en el primer render.
  // En re-renders posteriores la función NO se vuelve a ejecutar, por lo
  // tanto cambios de page/selectedLeague NO pueden re-disparar el tour.
  // Si la flag existe → showTour=false. Si no existe → showTour=true.
  //
  // 2026-05-10: bug observado — clientes entrando directo a /historial (sin
  // pasar antes por /dashboard) veían un overlay oscuro que tapaba la página.
  // Causa: el tour busca targets [data-tour="motor-banner"], [data-tour="kpi-enter"],
  // etc. que SOLO existen en LivePage. En SignalsHistory esos elementos no
  // existen → Joyride muestra steps huérfanos sobre overlay oscuro. El guard
  // de `run` abajo asegura que el tour solo corre cuando page==='live'.
  const [showTour, setShowTour] = useState(() => {
    try {
      return !localStorage.getItem(TOUR_STORAGE_KEY);
    } catch {
      return false;
    }
  });

  const { user: authUser, userData } = useAuth();
  const user = {
    uid: authUser?.uid || null,
    displayName: userData?.nombre || authUser?.displayName || 'Cliente',
    email: authUser?.email || null,
  };

  // Auto-reset defensivo: si selectedLeague está stale (no existe en
  // los picks actuales) por más de 5s, volver a 'all' para evitar que
  // el dashboard quede congelado en "no hay partidos" cuando la liga
  // que el user había seleccionado ya no tiene partidos en vivo.
  useEffect(() => {
    if (selectedLeague === 'all' || !selectedLeague) return;
    if (!picks || picks.length === 0) return;

    const target = normalizeLeague(selectedLeague);
    const ligasDisponibles = new Set(
      picks.map((p) => normalizeLeague(p.liga || p.league)).filter(Boolean)
    );
    if (ligasDisponibles.has(target)) return;

    const timer = setTimeout(() => {
      const stillNoMatch = picks.every(
        (p) => normalizeLeague(p.liga || p.league) !== target
      );
      if (stillNoMatch) {
        // eslint-disable-next-line no-console
        console.log('[ee_league] Reset filter — liga stale:', selectedLeague);
        setLeague('all');
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [picks, selectedLeague]);

  // Persistir ruta y liga.
  useEffect(() => { localStorage.setItem('ee_route', page); }, [page]);
  useEffect(() => { localStorage.setItem('ee_league', selectedLeague); }, [selectedLeague]);

  // Guard: si por alguna razón llega 'performance' o 'config' (admin-only),
  // forzamos 'live'. Defensa para residuos en localStorage o intentos manuales.
  useEffect(() => {
    if (!CLIENT_PAGES.has(page)) {
      setPage('live');
    }
  }, [page]);

  // Firebase RTDB listener — mismo patrón que AdminDashboard.
  useEffect(() => {
    const picksRef = ref(db, 'picks');
    const unsub = onValue(
      picksRef,
      (snapshot) => {
        // El snapshot llegó: estamos conectados. /picks vacío es estado
        // válido (SHADOW MODE no escribe ahí; o sin partidos en vivo) y
        // debe renderizar EmptyState, no "Conectando con Firebase...".
        setConnected(true);
        const data = snapshot.val();
        if (!data) { setPicks([]); return; }
        const list = Object.entries(data).map(([id, val]) => ({ id, ...val }));
        list.sort((a, b) => {
          const rank = (v) => {
            const d = (v.decision || '').toLowerCase();
            if (d.includes('under') || d.includes('over')) return 0;
            if (d.includes('early')) return 2;
            return 1;
          };
          return rank(a) - rank(b);
        });
        setPicks(list);
        if (list[0]?.timestamp) setLastUpdate(new Date(list[0].timestamp));
      },
      () => setConnected(false)
    );
    return () => unsub();
  }, []);

  // Tick cada 5s para refrescar timestamps.
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(interval);
  }, []);

  // useCallback estable para <OnboardingTour> + React.memo: cuando el padre
  // re-renderiza por cambios de state ajeno (filter/page), la referencia
  // de onFinish no cambia, así que OnboardingTour no se remonta y Joyride
  // no se reinicia.
  const handleTourFinish = useCallback(() => {
    setShowTour(false);
  }, []);

  function getAgeStr() {
    if (!lastUpdate) return '—';
    const secs = Math.floor((Date.now() - lastUpdate.getTime()) / 1000);
    if (secs < 10) return 'ahora';
    if (secs < 60) return `hace ${secs}s`;
    return `hace ${Math.floor(secs / 60)}m`;
  }

  function getAgeColor() {
    if (!lastUpdate) return 'var(--muted)';
    const secs = Math.floor((Date.now() - lastUpdate.getTime()) / 1000);
    if (secs < 15) return 'var(--pos)';
    if (secs < 30) return 'var(--warn)';
    return 'var(--neg)';
  }

  function handleNav(p, league) {
    // Doble-guard: si la página no es del cliente, ir a 'live'.
    const target = CLIENT_PAGES.has(p) ? p : 'live';
    if (league) setLeague(league);
    setSidebarOpen(false);
    // UX audit ISSUE C: cambio de pestaña navega a la URL correspondiente
    // para mantener URL ↔ page sincronizados (deeplink al historial).
    if (target === 'signals') {
      navigate('/historial');
    } else {
      navigate('/dashboard');
    }
    // setPage lo actualizamos también para que el render sincronice
    // antes de que la nueva ruta monte el componente con initialPage.
    setPage(target);
  }

  return (
    <DashboardThemeProvider>
    <div className="app-layout app">
      <Sidebar
        page={page}
        setPage={(p) => handleNav(p)}
        selectedLeague={selectedLeague}
        setLeague={(l) => handleNav('live', l)}
        picks={picks}
        user={user}
        userData={userData}
        isAdmin={false}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="main-content main">
        <Topbar
          connected={connected}
          lastUpdate={lastUpdate}
          getAgeStr={getAgeStr}
          getAgeColor={getAgeColor}
          onMenuOpen={() => setSidebarOpen(true)}
          picks={picks}
        />

        {/* Banner pausa técnica: visible para todos los clientes (no admin —
            este componente solo monta el flujo cliente). Aparece en ambas
            pestañas (live + signals) para que el aviso sea consistente. */}
        {PAUSA_TECNICA_ACTIVA && (
          <div style={{ padding: '14px 20px 0 20px' }}>
            <PausaTecnicaBanner />
          </div>
        )}

        {page === 'live' && (
          <LivePage
            picks={picks}
            connected={connected}
            selectedLeague={selectedLeague}
            user={user}
            userData={userData}
            tick={tick}
          />
        )}
        {page === 'signals' && <SignalsHistory />}
      </div>

      {/* Tour solo corre en page='live'. En /historial (page='signals') los
          targets del tour no existen y Joyride renderiza overlay huérfano que
          tapa la página — bug crítico que reportaba el cliente. */}
      <OnboardingTour run={showTour && page === 'live'} onFinish={handleTourFinish} />
    </div>
    </DashboardThemeProvider>
  );
}
